import { useApolloClient, useMutation } from "@apollo/client";
import { Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import { useState } from "react";

import type { InboundEmailRow } from "./senderDialogValues";

import { OrganizationPicker, type OrganizationOption } from "./OrganizationPicker";

const ROUTE_EMAIL = gql(`
  mutation RouteReceivedEmail($id: ID!, $tenantId: ID!) {
    replayReceivedEmail(id: $id, tenantId: $tenantId)
  }
`);

/**
 * Choose the organization an unplaceable email belongs to.
 *
 * This is the one decision the allowlist could not make — mail held from our own
 * staff, or from a vendor two organizations both claim — and it is the highest-stakes
 * click on the page: the attachments become that organization's documents. Hence a
 * dialog with the organization as its only field, naming the email it is about, and
 * a description that says what will happen, rather than a picker in the grid row.
 */
export function RouteDialog({
  email,
  onRouted,
  onClose,
}: {
  email: InboundEmailRow;
  /** Told once the request is accepted, so the row stops offering the same action. */
  onRouted?: () => void;
  onClose: () => void;
}) {
  const alerts = useAlerts();
  const apolloClient = useApolloClient();
  const [organization, setOrganization] = useState<OrganizationOption | null>(null);
  const [routeEmail, { loading }] = useMutation(ROUTE_EMAIL);

  const handleRoute = async () => {
    if (!organization) return;
    try {
      await routeEmail({ variables: { id: email.id, tenantId: organization.id } });
      alerts.success(
        `Routing to ${organization.name}. The row updates as intake runs; refresh in a moment.`
      );
      // Both inbound queries: the row's status and the opened detail panel read from
      // different fields of the cache.
      apolloClient.cache.evict({ fieldName: "receivedEmails" });
      apolloClient.cache.evict({ fieldName: "receivedEmail" });
      apolloClient.cache.gc();
      onRouted?.();
      onClose();
    } catch (err) {
      alerts.error(err instanceof Error ? err.message : "Couldn't route that email.", err);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title="Route to an organization"
      description={
        organization ? (
          <>
            The attachments will be filed as <strong>{organization.name}</strong>'s documents and
            parsed, and the sender will receive the usual confirmation.
          </>
        ) : (
          "Intake runs again with the organization you choose, instead of the allowlist deciding."
        )
      }
      primaryActionButtonProps={{
        saveText: "Route",
        onClick: handleRoute,
        loading,
        disabled: loading || !organization,
      }}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Stack spacing={0.25}>
          <Typography variant="caption" color="text.secondary">
            Email
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
            {email.subject || "(no subject)"}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
            from {email.fromAddress ?? "unknown sender"}
          </Typography>
        </Stack>
        <OrganizationPicker
          value={organization}
          onChange={setOrganization}
          helperText="Whose documents these attachments will become. Expand the row first if you need to see what is attached."
        />
      </Stack>
    </Dialog>
  );
}
